const FRESHNESS_QUERY_KEY = "__ap_shell_freshness";
const CHECK_THROTTLE_MS = 5000;

function assetPath(value, origin) {
  try {
    return new URL(value, origin).pathname;
  } catch (_error) {
    return "";
  }
}

function isEditableElement(element) {
  if (!element) return false;
  const tagName = String(element.tagName || "").toLowerCase();
  return (
    tagName === "input"
    || tagName === "textarea"
    || tagName === "select"
    || element.isContentEditable === true
  );
}

export function shouldDeferStaleShellReload(doc) {
  if (!doc?.querySelector) return false;

  // Never interrupt a person who is actively typing/selecting, confirming a
  // dialog action, or sitting on a screen that explicitly reports unsaved
  // admin/seller edits. A later focus/visibility event will re-check.
  if (isEditableElement(doc.activeElement)) return true;
  if (doc.querySelector('[role="dialog"], dialog[open]')) return true;

  // These states represent work that must not be interrupted even when there
  // is no focused input at the instant the tab resumes.
  if (doc.querySelector(
    '[data-testid="checkout-progress"], [data-testid="embedded-payment-handoff"]',
  )) return true;

  const knownUnsaved = [
    '[data-testid="mp-fees-unsaved-pill"]',
    '[data-testid="vp-save-status"]',
  ];

  return knownUnsaved.some((selector) => {
    const node = doc.querySelector(selector);
    return Boolean(node && /unsaved/i.test(String(node.textContent || "")));
  });
}

export function appShellSignature(doc, origin = window.location.origin) {
  if (!doc?.querySelectorAll) return "";

  const paths = Array.from(
    doc.querySelectorAll('script[src], link[rel="stylesheet"][href]'),
  )
    .map((node) => node.getAttribute("src") || node.getAttribute("href") || "")
    .map((value) => assetPath(value, origin))
    .filter((path) => (
      /^\/static\/js\/(?:runtime-main|main)\.[^/]+\.js$/.test(path)
      || /^\/static\/css\/main\.[^/]+\.css$/.test(path)
    ))
    .sort();

  return paths.join("|");
}

export async function checkForStaleAppShell({
  fetchImpl = window.fetch.bind(window),
  currentDocument = document,
  locationObj = window.location,
  reload = () => window.location.reload(),
  now = () => Date.now(),
} = {}) {
  const currentSignature = appShellSignature(currentDocument, locationObj.origin);
  if (!currentSignature) return false;

  // Always compare against the current deployed homepage entry document.
  // CRA's main JS/CSS fingerprints describe the whole application bundle, so
  // this check is valid regardless of which client-side route is visible.
  const freshnessUrl = new URL("/", locationObj.origin);
  freshnessUrl.searchParams.set(FRESHNESS_QUERY_KEY, String(now()));

  try {
    const response = await fetchImpl(freshnessUrl.toString(), {
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        Accept: "text/html",
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });

    if (!response?.ok) return false;

    const html = await response.text();
    const latestDocument = new DOMParser().parseFromString(html, "text/html");
    const latestSignature = appShellSignature(latestDocument, locationObj.origin);

    if (!latestSignature || latestSignature === currentSignature) return false;

    // A stale bundle can affect any route, including authenticated screens.
    // Reload the current URL only when doing so will not interrupt an active
    // edit/dialog. Cart contents are persisted by CartContext across reloads.
    if (shouldDeferStaleShellReload(currentDocument)) return false;

    reload();
    return true;
  } catch (_error) {
    // A failed freshness check must never interrupt ordering or browsing.
    return false;
  }
}

export function installAppShellFreshnessGuard() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return () => {};
  }

  let checking = false;
  let lastCheckAt = 0;

  const revalidate = () => {
    const now = Date.now();
    if (checking || now - lastCheckAt < CHECK_THROTTLE_MS) return;

    checking = true;
    lastCheckAt = now;
    void checkForStaleAppShell().finally(() => {
      checking = false;
    });
  };

  const onPageShow = (event) => {
    if (event.persisted) revalidate();
  };
  const onVisible = () => {
    if (document.visibilityState === "visible") revalidate();
  };
  const onFocus = () => {
    if (!document.hidden) revalidate();
  };

  window.addEventListener("pageshow", onPageShow);
  window.addEventListener("focus", onFocus);
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    window.removeEventListener("pageshow", onPageShow);
    window.removeEventListener("focus", onFocus);
    document.removeEventListener("visibilitychange", onVisible);
  };
}
